// Contents of this file was produced with a help of a code generation tool and
// subsequently reviewed and edited by a human.
// While some of the code was created by an AI assisted service, manual adjustments
// have been made to ensure correctness, readability, functionality and compliance
// to Avalara's coding standards and guidelines.
// Any future modifications should be approached with caution to avoid
// overwriting the manual changes.


// This source code is owned by Avalara Inc. Unauthorized copying of this file,
// via any medium, is strictly prohibited. Proprietary and confidential.

// For more information, refer to the documentation or contact the development team.

import {
  SBanner,
  SCol,
  SContainer,
  SRow,
  SSelect,
  STabs,
  XGuidedTours
} from '@avalara/skylab-sdk/react';
import _ from 'lodash';
import QueryString from 'query-string';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import LANGUAGES_CONTROLLER from '../../../app/controllers/languages';
import { API } from '../../controllers/dynamicReports';
import { TableConfig } from '../../utils/appConfig';
import { ReportFilterType } from '../../utils/enums';
import Logger, { LOG_LEVELS } from '../../utils/logger';
import useDynamicReportsTourGuide from '../../hooks/useDynamicReportsTourGuide';
import ExportAll from '../common/ExportAll';
import LanguageStringResolver from "../fields/language/LanguageStringResolver";
import SimpleDateSelector from '../selectors/date/SimpleDateSelector';
import DynamicReportsView from './DynamicReportsView';

const FORM_NAME = 'DYNAMIC_REPORTS_FORM';
const DYNAMIC_REPORTS_DOWNLOAD_PAGE_LABEL = 'Dynamic reports download';
/** @type {Logger} */
const log = new Logger(FORM_NAME, LOG_LEVELS.DEBUG);

const urlRoot = '/DynamicReports/';
const defaultPageData = {
  pageSize: TableConfig.PAGINATION.STANDARD_LEVEL_ONE_PAGE_SIZE,
  page: 1
};

export function isNullishOrEmpty(value) {
  return value === '' || value === undefined || value === null;
}

export function getCleanRangeValues(values) {
  const startVal = values?.[0];
  const endVal = values?.[1];
  const trimmedStart = typeof startVal === 'string' ? startVal.trim() : startVal;
  const trimmedEnd = typeof endVal === 'string' ? endVal.trim() : endVal;
  const startEmpty = isNullishOrEmpty(trimmedStart);
  const endEmpty = isNullishOrEmpty(trimmedEnd);
  if (startEmpty && endEmpty) {
    return null;
  }
  const startOut = startEmpty ? '' : trimmedStart;
  const endOut = endEmpty ? '' : trimmedEnd;
  return [startOut, endOut];
}

export function getCleanListValues(values) {
  if (!Array.isArray(values)) { return null; }
  const cleaned = [];
  for (const raw of values) {
    const val = typeof raw === 'string' ? raw.trim() : raw;
    if (!isNullishOrEmpty(val)) { cleaned.push(val); }
  }
  return cleaned.length ? cleaned : null;
}

export function buildSelectedFiltersFromUrl(filters, fieldValues) {
  const result = {};
  if (!Array.isArray(filters)) { return result; }
  const isRangeType = (t) => t === ReportFilterType.DATE_RANGE || t === ReportFilterType.NUMBER_RANGE;
  for (const filter of filters) {
    const raw = fieldValues?.[filter.id];
    if (raw === undefined) { continue; }
    const parts = String(raw).split(';');
    const valuesArray = isRangeType(filter.type) ? [parts[0] ?? '', parts[1] ?? ''] : parts;
    result[filter.id] = { fieldType: filter.type, fieldValues: valuesArray };
  }
  return result;
}

export function getCleanedSelectedFilter(filters) {
  const source = filters || {};
  return Object.entries(source).reduce((acc, [key, filterObj]) => {
    try {
      const type = filterObj?.fieldType;
      const rawValues = filterObj && filterObj.fieldValues; // Access inside try to catch getter errors
      const values = Array.isArray(rawValues) ? rawValues : [];
      if (!type) { return acc; }
      if (type === ReportFilterType.DATE_RANGE || type === ReportFilterType.NUMBER_RANGE) {
        const cleanedRange = getCleanRangeValues(values);
        if (cleanedRange) {
          acc[key] = { fieldType: type, fieldValues: cleanedRange };
        }
        return acc;
      }

      const cleanedValues = getCleanListValues(values);
      if (cleanedValues) {
        acc[key] = { fieldType: type, fieldValues: cleanedValues };
      }
      return acc;
    } catch (e) {
      log.warn('getCleanedSelectedFilter: error while cleaning filter', {
        key,
        filterObj,
        errorMessage: e && e.message,
        errorStack: e && e.stack
      });
      return acc;
    }
  }, {});
}

const DynamicReportForm = ({
   companies,
   customerId,
   doExportAllDynamicReportsToCSV,
   doHideToast,
   doLoadDynamicReportsData,
   doLoadLatestDynamicReportData,
   doLoadReportSpecification,
   doLoadReportSpecificationV5,
   doLoadLatestDynamicReportDefinitions,
   dynamicMenuItems,
   doResetReportData,
   error,
   featureFlags,
   history,
   languages,
   loading,
   loadingSpec,
   location,
   match,
   showToast,
   specifications,
   totalCount,
   userName,
 }) => {
  const [selectedCategory, setSelectedCategory] = useState(/** @type {string} */ match?.params?.category || 'transaction');
  const [pageData, setPageData] = useState({...defaultPageData});
  const [enableExportAll, setEnableExportAll] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState({});
  const isGuidedTourEnabled = featureFlags?.EnableDynamicReportsGuidedTour === true;
  const useDynamicReportingExportApi = featureFlags?.UseDynamicReportingExportApi === true;

  const categories = useMemo(() => (specifications?.values || specifications?.categories || []), [specifications]);

  // Tour guide hook - plug and play (handles navigation blocking internally)
  const {
    guidedToursRef,
    guidedTours,
    transactionCategoryId,
    tourReportId,
    startTour,
    markSpecLoadStarted
  } = useDynamicReportsTourGuide({
    isGuidedTourEnabled,
    userName,
    loading,
    loadingSpec,
    categories,
    history
  });

  const selectedReport = match?.params?.report || undefined;
  const fieldValues = location?.search?.length ? QueryString.parse(location.search) : {};
  const urlKey = location?.search || '';

  // Cache split values per URL change to avoid repeated split(';')
  const fieldValuesPartsCache = useMemo(() => {
    const cache = {};
    for (const key in fieldValues) {
      if (Object.prototype.hasOwnProperty.call(fieldValues, key)) {
        cache[key] = String(fieldValues[key]).split(';');
      }
    }
    return cache;
  }, [urlKey]);

  // Hydrate internal filter state from URL when report changes or navigation occurs
  const lastUrlKeyRef = useRef(urlKey);
  useEffect(() => {
    const spec = getReportSpec(selectedReport);
    if (!spec?.filters) { setSelectedFilter({}); return; }
    setSelectedFilter(buildSelectedFiltersFromUrl(spec.filters, fieldValues));
    lastUrlKeyRef.current = urlKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport]);

  useEffect(() => {
    if (urlKey === lastUrlKeyRef.current) { return; }
    const spec = getReportSpec(selectedReport);
    if (!spec?.filters) { return; }
    setSelectedFilter(buildSelectedFiltersFromUrl(spec.filters, fieldValues));
    lastUrlKeyRef.current = urlKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey]);

  // After specifications load (or change), if we have URL filters but state is empty, hydrate once
  useEffect(() => {
    const spec = getReportSpec(selectedReport);
    if (!spec?.filters) { return; }
    const fromUrl = buildSelectedFiltersFromUrl(spec.filters, fieldValues);
    const stateEmpty = !selectedFilter || Object.keys(selectedFilter).length === 0;
    const urlHasFilters = Object.keys(fromUrl).length > 0;
    if (stateEmpty && urlHasFilters) {
      setSelectedFilter(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specifications, selectedReport]);

  function normalizeFilterUpdate(prev, filter, nextValues) {
    const next = { ...prev };
    let valuesArray;
    if (Array.isArray(nextValues)) {
      valuesArray = nextValues;
    } else if (nextValues === undefined) {
      valuesArray = [];
    } else {
      valuesArray = [nextValues];
    }
    const isRange = filter.type === ReportFilterType.DATE_RANGE || filter.type === ReportFilterType.NUMBER_RANGE;
    const cleaned = isRange ? [valuesArray[0] ?? '', valuesArray[1] ?? ''] : valuesArray.filter(v => v !== '' && v !== undefined && v !== null);
    const shouldRemove = isRange ? (cleaned[0] === '' && cleaned[1] === '') : cleaned.length === 0;
    if (shouldRemove) {
      const removed = _.omit(next, [filter.id]);
      log.debug('filterUpdate: remove', { filterId: filter.id, type: filter.type });
      return removed;
    }
    next[filter.id] = { fieldType: filter.type, fieldValues: cleaned };
    log.debug('filterUpdate: update', { filterId: filter.id, type: filter.type, values: cleaned });
    return next;
  }

  function updateFilter(filter, nextValues) {
    const next = normalizeFilterUpdate(selectedFilter, filter, nextValues);
    setSelectedFilter(next);
    const nextVals = next[filter.id]?.fieldValues;
    onFilterValueUpdate(filter.id, nextVals);
  }

  function executeFetch(currentPage, currentPageSize) {
    const cleanedSelectedFilter = getCleanedSelectedFilter(selectedFilter);
    let params = {
      companyIds: companies?.map(company => company.id),
      reportType: selectedReport,
      pageSize: currentPageSize,
      pageNumber: currentPage,
      columnSchema: cleanedSelectedFilter
    };

    if(featureFlags?.EnableDynamicReportAPIReviewChanges) {
      delete params.columnSchema;
      params = {
        ...params,
        filterCriteria: cleanedSelectedFilter,
        userName: userName,
        tenantId: customerId
      }
      doLoadLatestDynamicReportData(params);
    } else {
      doLoadDynamicReportsData(params, featureFlags?.EnableAdditionalLoggingInSF);
    }
    setEnableExportAll(false);
    doHideToast();
  }

  // URL is the single source of truth; defaults come from fieldValuesPartsCache

  const updatePageData = useCallback(newPageData => {
    // only update state if props on new value differ from old value
    setPageData(
      prevState => _.isEqual(prevState, newPageData) ? prevState : newPageData
    );
  }, []);

  const onSubmit = useCallback(
    (isButtonClick) => {
      if (!selectedReport) {
        return;
      }

      if (isButtonClick) {
        setHasSubmitted(true);
        const targetPage = 1;
        if (pageData.page !== targetPage) {
          updatePageData({ ...pageData, page: targetPage });
        }
        executeFetch(targetPage, pageData.pageSize);
        return;
      }

      // Non-button-triggered submit (not used now), keep fallback
      executeFetch(pageData.page, pageData.pageSize);
    },
    [
      selectedReport,
      updatePageData,
      companies,
      pageData,
      hasSubmitted,
      selectedFilter,
      userName,
      customerId,
      doLoadLatestDynamicReportData,
      doLoadDynamicReportsData,
      featureFlags,
      doHideToast
    ]
  );

  function onSelectCategory(category) {
    if (!category || selectedCategory !== category) {
      setHasSubmitted(false);
      setSelectedCategory(!category?selectedCategory:category);
      updatePageData({...defaultPageData});
      doResetReportData();
    }
  }

  function onSelectReport(report) {
    if (selectedReport !== report) {
      setHasSubmitted(false);
      history.replace(urlRoot + selectedCategory + '/' + report);
      setSelectedFilter({});
      updatePageData({...defaultPageData});
      doResetReportData();
    }
  }

  function onDeselectReport() {
    setHasSubmitted(false);
    history.replace(urlRoot + selectedCategory);
    setSelectedFilter({});
    updatePageData({...defaultPageData});
    doResetReportData();
  }

  function onFilterValueUpdate(filterName, value) {
    setHasSubmitted(false);
    if (Array.isArray(value)) {
      value = value.join(';');
    }

    if (value === '') {
      value = undefined;
    }

    const newFieldValues = value === undefined
      ? _.omit(fieldValues, [filterName])
      : {
          ...fieldValues,
          [filterName]: value
        };

    const queryString = QueryString.stringify(newFieldValues);
    let url = urlRoot + selectedCategory + '/' + selectedReport;
    if (queryString?.length) {
      url += '?' + queryString;
    }

    history.replace(url);
    lastUrlKeyRef.current = queryString?.length ? ('?' + queryString) : '';
  }

  function onSelectValueChange(isMultiSelect, value, filter) {
    let newFieldValues = [value];

    if (isMultiSelect) {
      const values = selectedFilter[filter.id]?.fieldValues || fieldValuesPartsCache[filter.id] || [];
      newFieldValues = [...values, value];
    }

    updateFilter(filter, newFieldValues);
  }

  function onDeselectFromSelect(isMultiSelect, removedValue, filter) {
    let newFieldValues = [];

    if (isMultiSelect) {
      const oldValues = selectedFilter[filter.id]?.fieldValues || fieldValuesPartsCache[filter.id] || [];
      newFieldValues = _.filter(oldValues, value => value !== removedValue);
    } else {
      newFieldValues = [];
    }

    updateFilter(filter, newFieldValues);
  }

  // Removed auto-submit effect; fetching is explicit on submit or pagination changes

  const handlePageSizeChange = (pageSize) => {
    const newSize = +pageSize;
    updatePageData({ page: 1, pageSize: newSize });
    if (hasSubmitted) {
      executeFetch(1, newSize);
    }
  }

  const handlePageChange = (page) => {
    const newPage = page + 1;
    updatePageData({ ...pageData, page: newPage });
    if (hasSubmitted) {
      executeFetch(newPage, pageData.pageSize);
    }
  }

  const handleRangeSelection = (filter, value, period) => {
    let current;
    if (selectedFilter[filter.id]?.fieldValues) {
      current = [...selectedFilter[filter.id].fieldValues];
    } else if (fieldValuesPartsCache[filter.id]) {
      current = [...fieldValuesPartsCache[filter.id]];
    } else {
      current = ['', ''];
    }
    if (period === 'start') {
      current[0] = value;
    } else if (period === 'end') {
      current[1] = value;
    }
    updateFilter(filter, current);
  }

  const handleTextUpdate = (filter, value) => {
    const raw = value ?? '';
    const tokens = raw.split(',').map(v => v.trim()).filter(Boolean);

    const next = { ...selectedFilter };
    if (raw.length === 0) {
      // user cleared completely: remove entry and URL param
      if (next[filter.id]) { delete next[filter.id]; }
      setSelectedFilter(next);
      onFilterValueUpdate(filter.id, undefined);
      return;
    }

    next[filter.id] = {
      fieldType: filter.type,
      fieldValues: tokens,
      rawText: raw
    };
    setSelectedFilter(next);
    onFilterValueUpdate(filter.id, tokens.length ? tokens : undefined);
  }

  function getSelectedCategorySpec() {
    if (!selectedCategory) { return null; }
    if (!specifications) { return null; }
    const categoriesOrValues = specifications?.values || specifications?.categories;
    return _.find(categoriesOrValues, category => category.id === selectedCategory);
  }

  function getReportSpec(reportId) {
    const selectedCategorySpec = getSelectedCategorySpec();

    if (!selectedCategorySpec?.reports) { return null; }

    return _.find(selectedCategorySpec.reports, report => report.id === reportId);
  }

  function renderDateRange(filter, className) {
    const startId = filter.id + 'Start';
    const endId = filter.id + 'End';
    let defaultValues = selectedFilter[filter.id]?.fieldValues || fieldValuesPartsCache[filter.id];
    return <>
      <SCol span="md-6 4">
        <label id={startId + 'Label'} htmlFor={startId} className={className}>
          {filter.name} start
        </label>
        <SimpleDateSelector
          dataTestID={startId}
          aria-labelledby={startId + 'Label'}
          selectionHandler={(data) => handleRangeSelection(filter, data[startId], 'start')}
          format="yyyy-mm-dd"
          fieldName={startId}
          value={defaultValues?.[0] || ''}
        />
      </SCol>
      <SCol span="md-6 4">
        <label id={endId + 'Label'} htmlFor={endId} className={className}>
          {filter.name} end
        </label>
        <SimpleDateSelector
          dataTestID={endId}
          aria-labelledby={endId + 'Label'}
          selectionHandler={(data) => handleRangeSelection(filter, data[endId], 'end')}
          format="yyyy-mm-dd"
          fieldName={endId}
          value={defaultValues?.[1] || ''}
        />
      </SCol>
    </>;
  }

  function renderNumberRange(filter, className) {
    const minId = filter.id + 'Min';
    const maxId = filter.id + 'Max';
    let defaultValues = selectedFilter[filter.id]?.fieldValues || fieldValuesPartsCache[filter.id];
    return <>
      <SCol span="md-6 4">
        <label htmlFor={minId} className={className}>{filter.name} min</label>
        <input
          id={minId}
          type="number"
          value={defaultValues?.[0] || ''}
          onChange={e => handleRangeSelection(filter, e.target.value, 'start')}
        />
      </SCol>
      <SCol span="md-6 4">
        <label htmlFor={maxId} className={className}>{filter.name} max</label>
        <input
          id={maxId}
          type="number"
          value={defaultValues?.[1] || ''}
          onChange={e => handleRangeSelection(filter, e.target.value, 'end')}
        />
      </SCol>
    </>;
  }

  function renderSelect(filter, className) {
    const selectedValues = _.defaultTo(selectedFilter[filter.id]?.fieldValues || fieldValuesPartsCache[filter.id], []);
    const optionsList = (featureFlags?.EnableDynamicReportsSpecV5Endpoint || featureFlags?.EnableDynamicReportAPIReviewChanges) ?
      _.find(_.defaultTo(specifications?.sharedData, []), d => d.dataKey === filter.dataKey)?.options :
      filter.options;

    const optionsListIsEmpty = !optionsList?.length;

    const selectOptions = optionsListIsEmpty ?
      [{
        value: '',
        label: 'No data available',
        selected: true
      }] :
      _.map(optionsList, option => ({
        key: option.id,
        value: option.name,
        label: option.name,
        selected: _.includes(selectedValues, option.id)
      }));

    return <SCol span="md-12 8">
      <label id={filter.id + 'Label'} htmlFor={filter.id} className={className}>
        {filter.name}
      </label>
      <SSelect
        aria-labelledby={filter.id + 'Label'}
        inputId={filter.id}
        optionsList={selectOptions}
        multiple={!!filter.allowMultiple}
        noSearch={!filter.enableSearch}
        selectionOptional={true}
        replacealloptions={true}
        disabled={optionsListIsEmpty}
        onS-select={e => onSelectValueChange(!!filter.allowMultiple, e.detail.item.key, filter)}
        onS-deselect={e => onDeselectFromSelect(!!filter.allowMultiple, e.detail.item.key, filter)}
      />
    </SCol>;
  }

  function renderText(filter, className) {
    const entry = selectedFilter[filter.id];
    const defaultValues = entry?.fieldValues || fieldValuesPartsCache[filter.id];
    let inputValue = '';
    if (typeof entry?.rawText === 'string') {
      inputValue = entry.rawText;
    } else if (defaultValues && Array.isArray(defaultValues)) {
      inputValue = defaultValues.join(',');
    }
    return <SCol span="md-12 8">
      <label htmlFor={filter.id} className={className}>{filter.name}</label>
      <input
        type="text"
        id={filter.id}
        onChange={e => handleTextUpdate(filter, e.target.value)}
        value={inputValue}
      />
    </SCol>;
  }

  function getJsxForFilter(filter) {
    const className = filter.isRequired ? 'required' : undefined;
    switch (filter.type) {
      case ReportFilterType.DATE_RANGE:
        return renderDateRange(filter, className);
      case ReportFilterType.NUMBER_RANGE:
        return renderNumberRange(filter, className);
      case ReportFilterType.SELECT:
        return renderSelect(filter, className);
      default:
        return renderText(filter, className);
    }
  }

  const tabItems = _.chain(specifications?.values || specifications?.categories)
    .map(category => ({
      id: category.id,
      label: category.name,
      tabPanelId: 'panel-' + category.id,
      selected: selectedCategory === category.id,
      sequence: category.sequence
    }))
    .concat({
      /*
        Tabs component sets the first tab as selected if no other is set as selected. This empty tab is unselectable
        by clicking, but means that it's possible to have no 'real' tabs selected.
      */
      id: '',
      label: '',
      tabPanelId: '',
      selected: !selectedCategory,
      sequence: ''
    })
    .value();

  const selectedCategorySpec = getSelectedCategorySpec();

  /** @type {{key: string, label: string, value: string, selected?: boolean}[]} */
  const reportSelectOptions = selectedCategorySpec && _.map(selectedCategorySpec.reports, report => ({
    key: report.id,
    label: report.name,
    value: report.id,
    selected: selectedReport === report.id
  }));

  useEffect(() => {
    if (!isInitialized && featureFlags) {
      setIsInitialized(true);
      markSpecLoadStarted(); // Notify tour guide that spec loading has started
      switch (true) {
        case featureFlags.EnableDynamicReportAPIReviewChanges:
          return doLoadLatestDynamicReportDefinitions(
            userName,
            customerId
          );
        case featureFlags.EnableDynamicReportsSpecV5Endpoint:
          return doLoadReportSpecificationV5();
        case featureFlags.EnableAdditionalLoggingInSF:
          return doLoadReportSpecification(true);
        default:
          return doLoadReportSpecification(false);
      }
    }
  }, [
    isInitialized,
    featureFlags,
    doLoadLatestDynamicReportDefinitions,
    userName,
    customerId,
    doLoadReportSpecification,
    doLoadReportSpecificationV5,
    markSpecLoadStarted,
  ]);

  useEffect(() => {
    if (!selectedReport) {
      updatePageData({...defaultPageData});
      doResetReportData();
    }
  }, [selectedReport, updatePageData, doResetReportData]);

  const getLanguageText = (languages, key) => {
    return showToast && languages && LANGUAGES_CONTROLLER.formatUtilFormatter(languages.data, key);
  }

  const exportAllLabel = useDynamicReportingExportApi ? "dynamicReport_GenerateCsvExport" : "dynamicReport_ExportToCSV";
  const exportAllToastTitle = getLanguageText(
    languages,
    useDynamicReportingExportApi ? "dynamicReport_GenerateCsvExportToastTitle" : "viewTransmission_ExportAllToastTitle"
  );
  const exportAllToastDetails = getLanguageText(
    languages,
    useDynamicReportingExportApi ? "dynamicReport_GenerateCsvExportToastDetails" : "viewTransmission_ExportAllToastDetails"
  );
  const dynamicReportsDownloadPageUrl = useMemo(() => {
    if (!Array.isArray(dynamicMenuItems)) {
      return null;
    }

    for (const menu of dynamicMenuItems) {
      const items = Array.isArray(menu?.menuItems) ? menu.menuItems : [];
      const downloadItem = items.find(item => item?.label === DYNAMIC_REPORTS_DOWNLOAD_PAGE_LABEL);
      if (downloadItem?.url) {
        return downloadItem.url;
      }
    }

    return null;
  }, [dynamicMenuItems]);

    // Enable Export All only when not loading and data is available
    useEffect(() => {
        if (loading) {
            setEnableExportAll(false);
            return;
        }
        setEnableExportAll(!!totalCount);
    }, [loading, totalCount]);

  const handleExportDynamicReport = () => {
    if (!selectedReport) {
      return;
    }
    const params = {
      companyIds: companies?.map(company => company.id),
      reportType: selectedReport,
      columnSchema: getCleanedSelectedFilter(selectedFilter),
      customerId: customerId,
      userName: userName
    }
    doExportAllDynamicReportsToCSV(params);
  }

  let renderedFilters = [];
  const selectedReportSpec = selectedReport && getReportSpec(selectedReport);
  if (selectedReportSpec?.filters) {
    renderedFilters = _.map(selectedReportSpec.filters, filter => ({
      id: filter.id,
      jsx: getJsxForFilter(filter)
    }));
  }

  const visuallyHiddenStyle = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0
  };

  const tourContainerStyle = {
    display: isGuidedTourEnabled && !loading && !loadingSpec ? undefined : 'none'
  };

  return <div data-testid="dynamic-report-form-component" className="content-area">
    <SContainer>
      {isGuidedTourEnabled && (
        <div style={tourContainerStyle} aria-hidden={loading || loadingSpec}>
          <XGuidedTours tours={guidedTours} ref={guidedToursRef} />
          {transactionCategoryId && (
            <button
              id="dynamic-reports-tab-transaction-trigger"
              type="button"
              onClick={() => onSelectCategory(transactionCategoryId)}
              style={visuallyHiddenStyle}
              aria-hidden="true"
              tabIndex={-1}
            />
          )}
          {tourReportId && (
            <button
              id="dynamic-reports-report-tour-trigger"
              type="button"
              onClick={() => onSelectReport(tourReportId)}
              style={visuallyHiddenStyle}
              aria-hidden="true"
              tabIndex={-1}
            />
          )}
          <button
            id="dynamic-reports-tour-complete-trigger"
            type="button"
            onClick={() => {}}
            style={visuallyHiddenStyle}
            aria-hidden="true"
            tabIndex={-1}
          />
        </div>
      )}
      <SRow>
        <SCol span="12">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <h1 id="dynamic-reports-title"><LanguageStringResolver string="TitleDynamicReports" /></h1>
            {isGuidedTourEnabled && (
              <button className="primary" onClick={startTour}>
                <LanguageStringResolver string="dynamic_reports_guided_tour" />
              </button>
            )}
          </div>
          <SBanner status="info" nodismiss={true}>
            <LanguageStringResolver string="dynamic_reports_banner" />
          </SBanner>
          <STabs
            id="dynamic-reports-category-tabs"
            data-testid="dynamic-report-form-category-tabs"
            tabItems={[...tabItems].sort((a, b) => a.sequence - b.sequence)}
            onS-select={e => onSelectCategory(e.detail.id)}
          />
        </SCol>
      </SRow>

      {error &&
        <div data-testid='error-container'>
          <s-alert key={error} status="error" data-testid='alert-error'>
            <span>{error}</span>
          </s-alert>
        </div>
      }

      {useDynamicReportingExportApi && showToast &&
        <div
          className="dynamic-reports-export-toast"
          data-testid="dynamic-reports-export-toast"
          role="status"
          aria-live="polite"
        >
          <div className="dynamic-reports-export-toast-panel" data-testid="dynamic-reports-export-banner">
            <button
              type="button"
              className="dynamic-reports-export-toast-close"
              data-testid="export-close-click"
              aria-label="Close export notification"
              onClick={doHideToast}
            >
              ×
            </button>
            <div className="dynamic-reports-export-toast-title">{exportAllToastTitle}</div>
            <div className="dynamic-reports-export-toast-message">{exportAllToastDetails}</div>
            <div className="dynamic-reports-export-toast-link-row">
              <a
                href={dynamicReportsDownloadPageUrl}
                className="dynamic-reports-export-toast-link"
                data-testid="dynamic-reports-export-link"
              >
                <LanguageStringResolver string="dynamicReport_ViewExportedReports" />
              </a>
            </div>
          </div>
        </div>
      }

      {selectedCategory && <SRow>
        <SCol span="md-12 8">
          <h2><LanguageStringResolver string="dynamic_reports_select_title" /></h2>
          <div>
            <label className='mb15'>
              <LanguageStringResolver string='dynamic_reports_RequiredFields' />
            </label>
          </div>
          <label id="reportTypeLabel"><LanguageStringResolver string="dynamic_reports_type" /></label>
          <SSelect
            aria-labelledby="reportTypeLabel"
            data-testid="dynamic-report-form-report-type-select"
            inputId="reportType"
            multiple={false}
            optionsList={reportSelectOptions}
            onS-select={e => onSelectReport(e.detail.item.key)}
            onS-deselect={onDeselectReport}
            noSearch={true}
            selectionOptional={true}
            replacealloptions={true}
          />
        </SCol>
      </SRow>}

      {selectedReportSpec?.filters && <>
        {_.map(renderedFilters, filter => <SRow key={filter.id}>
          {filter.jsx}
        </SRow>)}
        <SRow>
          <SCol span="12">
            <button id="dynamic-reports-submit" className="primary" onClick={() => onSubmit(true)}><LanguageStringResolver string="dynamic_reports_submit_button" /></button>
            {!loading && totalCount && <ExportAll
              exportLabel={exportAllLabel}
              testId="export-all-dynamic-reports"
              exportClick={() => handleExportDynamicReport()}
              exportFor="ENABLE_EXPORT_ALL_DYNAMIC_REPORT"
              elementType='btn'
              showToast={!useDynamicReportingExportApi && showToast}
              elementClassName={['tertiary', 'divider-left', !enableExportAll ? 'disabled' : ''].filter(Boolean).join(' ')}
              toastTitle={exportAllToastTitle}
              toastDetails={exportAllToastDetails}
              onCloseClick={doHideToast}
            />}
          </SCol>
        </SRow>
      </>}
      <DynamicReportsView
        paginationData={pageData}
        onPageSizeChange={handlePageSizeChange}
        onPageChange={handlePageChange}
      />
    </SContainer>
  </div>;
};


const mapStateToProps = (state) => {
  return {
    specifications: state.dynamicReports?.specifications,
    error: state.dynamicReports?.error,
    companies: state.companies?.data,
    totalCount: state.dynamicReports?.reportsData?.totalCount,
    loading: state.dynamicReports?.loading,
    loadingSpec: state.dynamicReports?.loadingSpec,
    languages: state.languages,
    showToast: state.dynamicReports?.showToast,
    customerId: state.authentication?.data?.customerId?.toString(),
    userName: state.authentication?.data?.userName || "Unknown",
    featureFlags: state.featureFlag?.data,
    dynamicMenuItems: state.authentication?.dynamicMenuItems
  }
};

const mapDispatchersToProps = (dispatch) => {
  return {
    doLoadReportSpecification: (enableAdditionalLog) => {
      log.debug('doLoadReportSpecification: ', enableAdditionalLog);
      dispatch(API.doLoadReportSpecification(enableAdditionalLog));
    },
    doLoadReportSpecificationV5: () => {
      log.debug('doLoadReportsSpecificationV5');
      dispatch(API.doLoadReportSpecificationV5());
    },
    doLoadLatestDynamicReportDefinitions: (userName, customerId) => {
      log.debug('doLatestDynamicReportDefinitions');
      dispatch(API.doLoadLatestDynamicReportDefinitions(userName, customerId));
    },
    doLoadLatestDynamicReportData: (params) => {
      log.debug('doLoadLatestDynamicReportData: ', params);
      dispatch(API.doLoadLatestDynamicReportData(params));
    },
    doLoadDynamicReportsData: (params, enableAdditionalLog) => {
      log.debug('doLoadDynamicReportsData: ', params);
      dispatch(API.doLoadDynamicReportsData(params, enableAdditionalLog));
    },
    doResetReportData: () => {
      dispatch(API.doResetReportData());
    },
    doExportAllDynamicReportsToCSV: (params) => {
      log.debug('doExportAllDynamicReportsToCSV: ', params);
      dispatch(API.doExportAllDynamicReportsToCSV(params));
    },
    doHideToast: () => {
      dispatch(API.doHideToast());
    }
  }
};

export default connect(mapStateToProps, mapDispatchersToProps)(DynamicReportForm);
